import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * SlideListItem wraps a child thumbnail to provide sorting and selection visuals.
 *
 * @param {object} params - Component parameters container
 * @param {string} params.id - Unique id for sortable item
 * @param {boolean} params.isSelected - Whether the item is currently selected
 * @param {boolean} params.isEditMode - Enables drag handle when true
 * @param {function(): void} params.onClick - Click handler for selection
 * @param {React.ReactNode} params.children - Thumbnail content
 * @param {boolean} params.isCompleted - Whether the section is completed (USER_VIEW mode)
 * @param {boolean} params.isAccessible - Whether the section can be accessed (USER_VIEW mode)
 * @param {string} params.mode - Current course mode (EDIT, ADMIN_VIEW, USER_VIEW)
 */
export const SlideListItem = ({ id, isSelected, isEditMode, onClick, children, isCompleted = false, isAccessible = true, mode }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        zIndex: isDragging ? 1 : 0
    };

    const dragHandleProps = isEditMode ? { ...attributes, ...listeners } : {};

    // Build CSS classes based on state
    const classNames = [
        'slideListItem',
        isSelected && 'selected',
        isDragging && 'is-dragging',
        isCompleted && 'completed',
        !isAccessible && 'locked'
    ].filter(Boolean).join(' ');

    // Disable click for locked items
    const handleClick = () => {
        if (isAccessible && onClick) {
            onClick();
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={classNames}
            onClick={handleClick}
            {...dragHandleProps}
        >
            {children}
        </div>
    );
};


